import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import CheckoutLayout from "@/components/CheckoutLayout";
import { createEmbeddedSession, fetchCatalog, formatPrice } from "@/utils/checkout";
import { summaryFor } from "@/utils/checkoutSummary";
import { ShieldCheck, Lock, BadgeCheck, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

/** Cache publishable-key → Stripe promise so switching tiers doesn't reload. */
const stripeCache = new Map();
function getStripePromise(pk) {
  if (!pk) return null;
  if (!stripeCache.has(pk)) stripeCache.set(pk, loadStripe(pk));
  return stripeCache.get(pk);
}

export default function Checkout() {
  const { tierId } = useParams();
  const nav = useNavigate();
  const [status, setStatus] = useState({ loading: true, error: null });
  const [session, setSession] = useState(null); // { client_secret, session_id, publishable_key }
  const [tier, setTier] = useState(null);

  // Fetch catalog (for price) in parallel with creating the Stripe session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatus({ loading: true, error: null });
      try {
        const [cat, s] = await Promise.all([
          fetchCatalog().catch(() => ({ tiers: {} })),
          createEmbeddedSession(tierId),
        ]);
        if (cancelled) return;
        const t = (cat?.tiers || {})[tierId] || null;
        setTier(t);
        setSession(s);
        setStatus({ loading: false, error: null });
      } catch (e) {
        if (cancelled) return;
        const detail = e?.response?.data?.detail || e.message || "Could not start checkout";
        setStatus({ loading: false, error: detail });
      }
    })();
    return () => { cancelled = true; };
  }, [tierId]);

  const stripePromise = useMemo(() => {
    return session?.publishable_key ? getStripePromise(session.publishable_key) : null;
  }, [session?.publishable_key]);

  const summary = summaryFor(tierId);
  const price = tier?.price;
  const isPlan = tier?.type === "subscription_3x";

  if (status.loading) {
    return (
      <CheckoutLayout>
        <div className="vbs-cko-loading" data-testid="checkout-loading">
          <Loader2 className="animate-spin" size={22} style={{ marginRight: 10 }} />
          Preparing your secure checkout…
        </div>
      </CheckoutLayout>
    );
  }

  if (status.error || !session) {
    return (
      <CheckoutLayout>
        <div className="vbs-cko-grid" style={{ gridTemplateColumns: "1fr", maxWidth: 620 }}>
          <div className="vbs-cko-summary" data-testid="checkout-error">
            <div className="vbs-cko-eye" style={{ color: "#ff9a8a" }}>Checkout unavailable</div>
            <h1 className="vbs-cko-title">We couldn't <em>start</em> this order.</h1>
            <p style={{ color: "var(--gray)", lineHeight: 1.7, margin: "14px 0 20px" }}>
              {status.error || "Unknown error"}.
              <br /> Please head back to pricing and try again, or contact <a href="mailto:hello@viralbooksuite.com" style={{ color: "var(--gold)" }}>hello@viralbooksuite.com</a>.
            </p>
            <button className="mkt-btn mkt-btn-primary" onClick={() => nav("/#pricing")} data-testid="checkout-error-back">
              <ArrowLeft size={14} /> Back to Pricing
            </button>
          </div>
        </div>
      </CheckoutLayout>
    );
  }

  return (
    <CheckoutLayout>
      <div className="vbs-cko-grid">
        {/* LEFT: order summary */}
        <aside className="vbs-cko-summary" data-testid="checkout-summary">
          {summary && <div className="vbs-cko-eye">{summary.eyebrow}</div>}
          <h1 className="vbs-cko-title">
            {summary ? (
              <>
                You're activating <em>{summary.italic}</em>
              </>
            ) : (
              <>Review your <em>order</em></>
            )}
          </h1>

          <div style={{ fontSize: 13, color: "var(--gray)", marginTop: 6 }}>
            {summary?.name || tier?.name || "Viral Book Suite"}
          </div>

          <div className="vbs-cko-price" data-testid="checkout-price">
            <span className="amt">{formatPrice(price)}</span>
            <span className="once">
              {isPlan ? `${summary?.priceLabel || "× 3 monthly"}` : (summary?.priceLabel || "one-time")}
            </span>
          </div>

          <ul className="vbs-cko-list">
            {(summary?.features || []).map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>

          <div className="vbs-cko-trust-row">
            <span><ShieldCheck size={13} /> {summary?.guarantee || "14-day guarantee"}</span>
            <span><Lock size={13} /> 256-bit SSL</span>
            <span><BadgeCheck size={13} /> Stripe verified</span>
          </div>

          <p style={{ fontSize: 12, color: "var(--gray)", lineHeight: 1.65, marginTop: 22 }}>
            You'll be charged by <strong style={{ color: "var(--bright)" }}>Viral Book Suite</strong>.
            {isPlan && " Stripe will auto-cancel this plan after 3 monthly payments — you'll never be charged a 4th time."}
          </p>
        </aside>

        {/* RIGHT: Stripe embedded checkout */}
        <div className="vbs-cko-embed-wrap" data-testid="checkout-embed">
          {stripePromise ? (
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ clientSecret: session.client_secret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          ) : (
            <div className="vbs-cko-loading">
              <Loader2 className="animate-spin" size={22} style={{ marginRight: 10 }} />
              Connecting to Stripe…
            </div>
          )}
        </div>
      </div>
    </CheckoutLayout>
  );
}
